import { isSocialPlatform, ScheduledPostStatus, SocialPlatform } from '../types';
import type { CalendarPost } from '../types';

/**
 * The states the Calendar shows (design b10). Each has a shape and a word. `scheduled` holds the
 * posts that wait for their time and the posts that go out now.
 */
export const calendarStates = ['needsYou', 'failed', 'draft', 'scheduled', 'published', 'canceled'] as const;
export type CalendarState = (typeof calendarStates)[number];

/** The song filter value of the posts with no song. */
export const NO_SONG = 'none';

export interface CalendarFilterValues {
  /** Catalog item uuids, or `NO_SONG`. Empty: every song. */
  songs: string[];
  /** Empty: every channel. */
  channels: SocialPlatform[];
  /** Empty: every state but Canceled. */
  states: CalendarState[];
}

export const NO_FILTERS: CalendarFilterValues = { songs: [], channels: [], states: [] };

/** The Calendar state of a post status. */
export function calendarStateOf(status: ScheduledPostStatus): CalendarState {
  switch (status) {
    case 'awaitingManualPost':
      return 'needsYou';
    case 'failed':
      return 'failed';
    case 'draft':
      return 'draft';
    case 'posted':
      return 'published';
    case 'canceled':
      return 'canceled';
    case 'scheduled':
    case 'rendering':
    case 'ready':
    case 'publishing':
    default:
      return 'scheduled';
  }
}

/** The posts that a drag or the keyboard can move (owner rule: Needs you can move too). */
export function isMovablePost(post: Pick<CalendarPost, 'status'>): boolean {
  return post.status === 'draft' || post.status === 'scheduled' || post.status === 'awaitingManualPost';
}

function isCalendarState(value: string): value is CalendarState {
  return (calendarStates as readonly string[]).includes(value);
}

function splitList(value: string | null): string[] {
  if (value === null || value === '') {
    return [];
  }
  return [...new Set(value.split(',').filter((part) => part !== ''))];
}

/** Reads the filters from the query of the address. An unknown value is dropped. */
export function parseCalendarFilters(params: URLSearchParams): CalendarFilterValues {
  return {
    songs: splitList(params.get('songs')),
    channels: splitList(params.get('channels')).filter(isSocialPlatform),
    states: splitList(params.get('states')).filter(isCalendarState),
  };
}

/** Writes one filter into the query. An empty list removes the key. */
export function writeCalendarFilter(params: URLSearchParams, key: keyof CalendarFilterValues, values: string[]): void {
  if (values.length === 0) {
    params.delete(key);
  } else {
    params.set(key, values.join(','));
  }
}

export function hasActiveFilters(filters: CalendarFilterValues): boolean {
  return filters.songs.length > 0 || filters.channels.length > 0 || filters.states.length > 0;
}

/**
 * True when a post passes the filters: AND across the groups, OR inside a group (design a3).
 * With no state filter, a canceled post is hidden.
 */
export function passesFilters(post: CalendarPost, filters: CalendarFilterValues): boolean {
  const songKey = post.catalogItemUuid ?? NO_SONG;
  if (filters.songs.length > 0 && !filters.songs.includes(songKey)) {
    return false;
  }
  if (filters.channels.length > 0 && !filters.channels.includes(post.platform)) {
    return false;
  }
  const state = calendarStateOf(post.status);
  if (filters.states.length === 0) {
    return state !== 'canceled';
  }
  return filters.states.includes(state);
}

/** The count of each state, for the State chips. */
export function countByState(posts: CalendarPost[]): Record<CalendarState, number> {
  const counts: Record<CalendarState, number> = {
    needsYou: 0,
    failed: 0,
    draft: 0,
    scheduled: 0,
    published: 0,
    canceled: 0,
  };
  posts.forEach((post) => {
    counts[calendarStateOf(post.status)] += 1;
  });
  return counts;
}

/** Adds the value to the list, or removes it when it is in the list. */
export function toggleValue<T>(list: T[], value: T): T[] {
  return list.includes(value) ? list.filter((one) => one !== value) : [...list, value];
}
